import { useEffect, useState } from "react";
import { Box, Grid, Typography, Card, CardContent, Chip, Stack, Divider } from "@mui/material";
import { colors } from "../theme/theme";
import { statsApi } from "../api/client";
import { formatFileSize } from "../util/config";

export const ToolPageShell = ({ tool, children, tips = [] }) => {
    const Icon = tool.icon;
    const [stats, setStats] = useState(null);

    useEffect(() => {
        statsApi
            .summary()
            .then((s) => setStats(s.perTool?.[tool.id] || { count: 0, last7Days: 0, totalBytes: 0 }))
            .catch(() => setStats({ count: 0, last7Days: 0, totalBytes: 0 }));
    }, [tool.id]);

    return (
        <Box>
            {/* Hero */}
            <Box mb={3}>
                <Stack direction="row" alignItems="center" spacing={2} mb={1.5}>
                    <Box
                        sx={{
                            width: 48,
                            height: 48,
                            borderRadius: 2.5,
                            bgcolor: `${tool.accent}15`,
                            color: tool.accent,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                        }}
                    >
                        <Icon />
                    </Box>
                    <Box>
                        <Typography variant="overline" sx={{ color: colors.textMuted }}>
                            PDF Tool
                        </Typography>
                        <Typography variant="h4" sx={{ fontWeight: 700, letterSpacing: "-0.02em", lineHeight: 1 }}>
                            {tool.label}
                        </Typography>
                    </Box>
                </Stack>
                <Typography sx={{ color: colors.textMuted, maxWidth: 720 }}>
                    {tool.description}
                </Typography>
            </Box>

            <Grid container spacing={3}>
                {/* Main content */}
                <Grid item xs={12} md={8}>
                    <Card>
                        <CardContent sx={{ p: { xs: 2, md: 4 } }}>{children}</CardContent>
                    </Card>
                </Grid>

                {/* Right rail */}
                <Grid item xs={12} md={4}>
                    <Card sx={{ mb: 2 }}>
                        <CardContent>
                            <Typography variant="overline" sx={{ color: colors.textMuted }}>
                                Usage
                            </Typography>
                            <Typography
                                sx={{
                                    fontFamily: '"Outfit"',
                                    fontWeight: 700,
                                    fontSize: 32,
                                    letterSpacing: "-0.02em",
                                    lineHeight: 1.1,
                                    color: tool.accent,
                                }}
                            >
                                {stats ? stats.count.toLocaleString() : "—"}
                            </Typography>
                            <Typography variant="caption" sx={{ color: colors.textMuted }}>
                                total exports
                            </Typography>
                            <Divider sx={{ my: 1.5 }} />
                            <Stack direction="row" justifyContent="space-between">
                                <Box>
                                    <Typography variant="overline" sx={{ color: colors.textMuted }}>7d</Typography>
                                    <Typography sx={{ fontFamily: '"JetBrains Mono"', fontWeight: 600 }}>
                                        +{stats?.last7Days ?? 0}
                                    </Typography>
                                </Box>
                                <Box>
                                    <Typography variant="overline" sx={{ color: colors.textMuted }}>Data</Typography>
                                    <Typography sx={{ fontFamily: '"JetBrains Mono"', fontWeight: 600 }}>
                                        {formatFileSize(stats?.totalBytes || 0)}
                                    </Typography>
                                </Box>
                            </Stack>
                        </CardContent>
                    </Card>

                    {tips.length > 0 && (
                        <Card>
                            <CardContent>
                                <Typography variant="overline" sx={{ color: colors.textMuted }}>
                                    Tips
                                </Typography>
                                <Stack spacing={1.5} mt={1}>
                                    {tips.map((tip, i) => (
                                        <Stack key={i} direction="row" spacing={1.5}>
                                            <Box
                                                sx={{
                                                    flexShrink: 0,
                                                    width: 22,
                                                    height: 22,
                                                    borderRadius: "50%",
                                                    bgcolor: colors.surfaceAlt,
                                                    fontFamily: '"JetBrains Mono"',
                                                    fontSize: 11,
                                                    fontWeight: 700,
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    color: colors.text,
                                                }}
                                            >
                                                {i + 1}
                                            </Box>
                                            <Typography sx={{ fontSize: 13, lineHeight: 1.55, color: colors.text }}>
                                                {tip}
                                            </Typography>
                                        </Stack>
                                    ))}
                                </Stack>
                            </CardContent>
                        </Card>
                    )}

                    <Card sx={{ mt: 2, bgcolor: colors.sidebarBg, color: "white", border: "none" }}>
                        <CardContent>
                            <Chip
                                label="100% FREE"
                                size="small"
                                sx={{
                                    bgcolor: colors.accent,
                                    color: "white",
                                    fontFamily: '"JetBrains Mono"',
                                    fontSize: 9,
                                    fontWeight: 700,
                                    letterSpacing: "0.1em",
                                    height: 20,
                                    mb: 1,
                                }}
                            />
                            <Typography sx={{ fontSize: 13, color: colors.sidebarMuted, lineHeight: 1.6 }}>
                                Your files are processed and discarded immediately. No storage, no tracking,
                                no signup.
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
        </Box>
    );
};
